import { useCallback, useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FlowSelectionsActions,
  FlowsActions,
  getLanguagesLookups,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';
import {
  useProductDispatch,
  useProductSelector,
  PlansActions,
} from '@wajeez-web/stores/product';
import {
  FormInputController,
  WajeezFormInputV2,
} from '@wajeez-web/components/form-builder';
import { Autocomplete } from '@wajeez-web/components/mui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import { useGetLanguagesLookups } from '@wajeez-web/components/helpers';

interface FlowSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  flowSelectionId?: number | undefined;
}

type FormInput = Server.UpdateFlowSelectionInput;

const formInputInitialValues: FormInput = {
  flowId: undefined,
  languageId: undefined,
  notes: undefined,
  planId: undefined,
  platform: undefined,
  isoTwoDigitCountryCode: undefined,
  majorVersion: undefined,
  middleVersion: undefined,
  minorVersion: undefined,
  rewardingDate: undefined,
  activeFrom: undefined,
  activeTo: undefined,
  userGroups: undefined,
  readerId: undefined,
  priority: undefined,
};

const platformsValues = Object.values(Server.Platform).filter(
  (v) => typeof v === 'string'
) as (keyof typeof Server.Platform)[];

const platforms: { value: Server.Platform; label: string | undefined }[] =
  platformsValues.map((v) => {
    return {
      value: Server.Platform[v],
      label: v,
    };
  });

const userGroupsValues = Object.values(Server.UserGrouping).filter(
  (v) => typeof v === 'string'
) as (keyof typeof Server.UserGrouping)[];

const userGroups: {
  value: Server.UserGrouping;
  label: string | undefined;
}[] = userGroupsValues.map((v) => {
  return {
    value: Server.UserGrouping[v],
    label: v,
  };
});

export const FlowSelectionModal = ({
  isOpen,
  onClose,
  onSubmit,
  flowSelectionId,
}: FlowSelectionModalProps) => {
  const contentDispatch = useContentDispatch();
  const productDispatch = useProductDispatch();

  const flowsLookups = useContentSelector(({ flows }) => flows.flowsLookups);

  const plansLookupsStatus = useProductSelector(({ plans }) => plans.status);
  const plansLookups = useProductSelector(({ plans }) => plans.data);

  const createFlowSelectionStatus = useContentSelector(
    ({ flowSelections }) => flowSelections.createFlowSelection.status
  );
  const editFlowSelectionStatus = useContentSelector(
    ({ flowSelections }) => flowSelections.editFlowSelection.status
  );
  const flowSelection = useContentSelector(
    ({ flowSelections }) => flowSelections.flowSelectionById
  );

  const languages = useGetLanguagesLookups();

  const isSaveLoading =
    createFlowSelectionStatus === 'loading' ||
    editFlowSelectionStatus === 'loading';

  const { control, handleSubmit, setValue, reset } = useForm<FormInput>({
    defaultValues: formInputInitialValues,
  });

  const onFormSubmit: SubmitHandler<FormInput> = async (data) => {
    const mappedData: FormInput = {
      ...data,
      flowId: Number(data.flowId),
      planId: Number(data.planId),
      platform: Number(data.platform),
      userGroups: data.userGroups?.map((g) => Number(g)),
    };

    let res: any;

    if (!flowSelectionId) {
      res = await contentDispatch(
        FlowSelectionsActions.createFlowSelectionAsync(mappedData)
      );
    } else {
      res = await contentDispatch(
        FlowSelectionsActions.editFlowSelectionAsync(mappedData)
      );
    }

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && onSubmit();
    }
  };

  useEffect(() => {
    if (flowSelection.data) {
      reset(flowSelection.data);
    }
  }, [flowSelection.data]);

  useEffect(() => {
    getLanguagesLookups();

    contentDispatch(FlowsActions.getFlowsLookupsAsync({}));

    productDispatch(PlansActions.getAllPlansAsync());
  }, [contentDispatch]);

  useEffect(() => {
    if (flowSelectionId) {
      contentDispatch(
        FlowSelectionsActions.getFlowSelectionByIdAsync(flowSelectionId)
      );
    }

    return () => {
      contentDispatch(FlowSelectionsActions.clearFlowSelection());
    };
  }, [flowSelectionId]);

  return (
    <Modal
      title="Flow Selection"
      withScroll={true}
      open={isOpen}
      onClose={onClose}
      handleAlert={false}
      alertTarget="modal"
      fontClassName="font-['Tajawal']"
    >
      {flowsLookups.status === 'loading' ||
      plansLookupsStatus === 'loading' ||
      languages.status === 'loading' ||
      flowSelection.status === 'loading' ? (
        <div className="w-full min-h-[500px] flex justify-center items-center">
          <Spin />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="w-full min-h-[500px] flex flex-col justify-start items-start font-['Tajawal']"
          noValidate
        >
          <div className="w-[300px] flex flex-col justify-start items-start space-y-3">
            <div className="w-full">
              <FormInputController
                control={control}
                name={`flowId`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                  required: true,
                }}
                renderInput={({ ...props }) => (
                  <Autocomplete
                    id={`flowId`}
                    options={flowsLookups.data.map((f) => {
                      return {
                        value: f.id,
                        label: f.flowName,
                      };
                    })}
                    label="Flow"
                    color="#1f7983"
                    required
                    {...props}
                  />
                )}
              />
            </div>
            <div className="w-full">
              <FormInputController
                control={control}
                name={`languageId`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <Autocomplete
                    id={`languageId`}
                    options={languages.data.map((l) => {
                      return {
                        value: l.id,
                        label: l.name,
                      };
                    })}
                    label="Language"
                    color="#1f7983"
                    {...props}
                  />
                )}
              />
            </div>
            <WajeezFormInputV2
              control={control}
              name={`notes`}
              fieldMeta={{
                fieldType: Server.FieldType.AreaText,
                fieldOptions: {},
                label: 'Notes',
                placeHolder: 'Notes',
              }}
              id={`notes`}
            />
            <div className="w-full">
              <FormInputController
                control={control}
                name={`planId`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <Autocomplete
                    id={`planId`}
                    options={plansLookups.map((p) => {
                      return {
                        value: p.id,
                        label: p.referenceName,
                      };
                    })}
                    label="Plan"
                    color="#1f7983"
                    {...props}
                  />
                )}
              />
            </div>
            <div className="w-full">
              <FormInputController
                control={control}
                name={`platform`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <Autocomplete
                    id={`platform`}
                    options={platforms.map((p) => {
                      return {
                        value: p.value,
                        label: p.label,
                      };
                    })}
                    label="Platform"
                    color="#1f7983"
                    {...props}
                  />
                )}
              />
            </div>
            <WajeezFormInputV2
              control={control}
              name={`isoTwoDigitCountryCode`}
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                fieldOptions: {
                  maxLength: 2,
                  minLength: 2,
                },
                label: 'Country Code',
                placeHolder: 'Country Code',
                onChange: (value) => {
                  setValue('isoTwoDigitCountryCode', value.toUpperCase());
                },
              }}
              id={`country-code`}
              validationErrorMessages={{
                maxLength: 'This field should consist of 2 letters',
                minLength: 'This field should consist of 2 letters',
              }}
            />
            <WajeezFormInputV2
              control={control}
              name={`majorVersion`}
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                fieldOptions: {},
                label: 'Major Version',
                placeHolder: 'Major Version',
              }}
              id={`major-version`}
            />
            <WajeezFormInputV2
              control={control}
              name={`middleVersion`}
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                fieldOptions: {},
                label: 'Middle Version',
                placeHolder: 'Middle Version',
              }}
              id={`middle-version`}
            />
            <WajeezFormInputV2
              control={control}
              name={`minorVersion`}
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                fieldOptions: {},
                label: 'Minor Version',
                placeHolder: 'Minor Version',
              }}
              id={`minor-version`}
            />
            <WajeezFormInputV2
              control={control}
              name={`rewardingDate`}
              fieldMeta={{
                fieldType: Server.FieldType.Switch,
                fieldOptions: {},
                label: 'Rewarding Date',
              }}
              id={`rewarding-date`}
            />
            <div className="w-full">
              <WajeezFormInputV2
                control={control}
                name={`activeFrom`}
                fieldMeta={{
                  fieldType: Server.FieldType.DateTime,
                  fieldOptions: {},
                  label: 'Active From',
                  placeHolder: 'Active From',
                }}
                id={`active-from`}
              />
            </div>
            <div className="w-full">
              <WajeezFormInputV2
                control={control}
                name={`activeTo`}
                fieldMeta={{
                  fieldType: Server.FieldType.DateTime,
                  fieldOptions: {},
                  label: 'Active To',
                  placeHolder: 'Active To',
                }}
                id={`active-to`}
              />
            </div>
            <div className="w-full">
              <FormInputController
                control={control}
                name={`userGroups`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <Autocomplete
                    id={`user-groups`}
                    options={userGroups.map((g) => {
                      return {
                        value: g.value,
                        label: g.label,
                      };
                    })}
                    label="User Groups"
                    color="#1f7983"
                    multiple
                    heightClassName="h-auto"
                    {...props}
                  />
                )}
              />
            </div>
            <WajeezFormInputV2
              control={control}
              name={`readerId`}
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                fieldOptions: {},
                label: 'Reader Id',
                placeHolder: 'Reader Id',
              }}
              id={`reader-id`}
            />
            <WajeezFormInputV2
              control={control}
              name={`priority`}
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                fieldOptions: {},
                label: 'Priority',
                placeHolder: 'Priority',
              }}
              id={`priority`}
            />
          </div>
          <div className="w-full pt-8 pb-4 flex justify-end items-center space-s-2">
            <div className="w-[70px]">
              <ButtonHeadless
                borderRadiusStyle="rounded-[6px]"
                paddingYStyle="py-[6px]"
                bgStyle="bg-warmGray-300"
                action={onClose}
              >
                <div className="text-[15px] text-black font-medium">
                  <ReturnTranslatedText
                    text="buttons.Cancel"
                    origintext="Cancel"
                  />
                </div>
              </ButtonHeadless>
            </div>
            <div className="w-[70px]">
              <ButtonHeadless
                borderRadiusStyle="rounded-[6px]"
                paddingYStyle="py-[6px]"
                type="submit"
                isDisabled={isSaveLoading}
                bgStyle={
                  !isSaveLoading ? 'bg-primary' : 'bg-primary opacity-70'
                }
              >
                <div className="text-[15px] text-white font-medium flex justify-center items-center space-s-1">
                  <ReturnTranslatedText text="buttons.Save" origintext="Save" />
                  {isSaveLoading && (
                    <BiLoaderAlt className="animate-spin w-5" />
                  )}
                </div>
              </ButtonHeadless>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};
